// Phase 83z.14 — Hooks for Vietnam administrative units (post-2025 merger).
//
// Backend (`routes/vn_admin.py`) seeds 34 provinces + 3321 wards into Mongo
// once at startup and exposes:
//   GET /api/public/vn/provinces
//   GET /api/public/vn/wards?province_code={code}
//
// Both endpoints are public and cache-friendly — we keep them in react-query
// with a 24h `staleTime` so the app virtually never re-fetches in a session.
//
// Storage convention on user.province_code / user.ward_code:
//   province_code  →  "01", "79"  (2-digit code from official data)
//   ward_code      →  "00004"      (full ward code, NOT name-encoded)
//
// Legacy users with "VN-HCM"-style province codes were reset to null at seed
// time so they re-pick under the new units.

import { useQuery } from "@tanstack/react-query";
import { edgeGet } from "../lib/edgeApi";

const QK_PROVINCES = ["vn", "provinces"];

export function useVnProvinces() {
  const q = useQuery({
    queryKey: QK_PROVINCES,
    queryFn: () => edgeGet("/public/vn/provinces", { policyKey: "vn_provinces" }).then((r) => r.data),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  return {
    provinces: q.data || [],
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

export function useVnWards(provinceCode) {
  const q = useQuery({
    queryKey: ["vn", "wards", provinceCode || "_"],
    enabled: !!provinceCode,
    queryFn: () =>
      edgeGet(`/public/vn/wards?province_code=${encodeURIComponent(provinceCode)}`, { policyKey: "vn_wards" })
        .then((r) => r.data),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  return {
    wards: q.data || [],
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** Lookup a province `{code, name}` from a list by code. */
export function findProvinceByCode(provinces, code) {
  if (!code) return null;
  return (provinces || []).find((p) => p.code === code) || null;
}

/** Lookup a ward `{code, name, province_code}` from a list by code. */
export function findWardByCode(wards, code) {
  if (!code) return null;
  return (wards || []).find((w) => w.code === code) || null;
}
